import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import {
  Avatar,
  AvatarFallback,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  Separator,
  Spinner,
} from '@repo/shared-client/components/ui'
import { useFormContext } from '@repo/shared-client/form'
import { cn, mergeRefs } from '@repo/shared-client/lib/utils'
import { useTranslation } from '@web/src/lib/intl/react'
import { UserIcon, XIcon } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import { ChangeEvent, forwardRef, useId, useImperativeHandle, useRef, useState } from 'react'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type { ConversationProfileSearchQuery, ConversationProfileSearchQueryVariables } from 'types/graphql'
import SFProfileBanner from '../sf-profile-banner/sf-profile-banner'

export const PROFILE_SEARCH: TypedDocumentNode<
  ConversationProfileSearchQuery,
  ConversationProfileSearchQueryVariables
> = gql`
  query ConversationProfileSearchQuery($filter: ProfileSearchFilter!, $cursor: String) {
    profileSearch: profileSearchCursor(filter: $filter, cursor: $cursor) {
      entries {
        ...ProfileInfo
      }
      cursor
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

type ProfileSearchEntry = NonNullable<
  NonNullable<NonNullable<ConversationProfileSearchQuery['profileSearch']>['entries']>[number]
>

type ComponentRef = {
  reset: () => void
} | null

type PopoverPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end'

function popoverPlacementFromProp(placement: PopoverPlacement | undefined): {
  side: 'top' | 'bottom' | 'left' | 'right'
  align: 'start' | 'center' | 'end'
  sideOffset: number
} {
  const map: Record<
    PopoverPlacement,
    { side: 'top' | 'bottom' | 'left' | 'right'; align: 'start' | 'center' | 'end' }
  > = {
    top: { side: 'top', align: 'center' },
    bottom: { side: 'bottom', align: 'center' },
    left: { side: 'left', align: 'center' },
    right: { side: 'right', align: 'center' },
    'top-start': { side: 'top', align: 'start' },
    'top-end': { side: 'top', align: 'end' },
    'bottom-start': { side: 'bottom', align: 'start' },
    'bottom-end': { side: 'bottom', align: 'end' },
    'left-start': { side: 'left', align: 'start' },
    'left-end': { side: 'left', align: 'end' },
    'right-start': { side: 'right', align: 'start' },
    'right-end': { side: 'right', align: 'end' },
  }

  const resolved = map[placement ?? 'bottom'] ?? { side: 'bottom', align: 'center' }
  return { ...resolved, sideOffset: 4 }
}

export type SFProfileSearchFieldProps = Omit<ComponentProps<typeof Field>, 'children'> & {
  name?: string
  required?: boolean
  label?: ReactNode
  helperText?: ReactNode
  optionalText?: ReactNode
  disabled?: boolean
  popOverPosition?: PopoverPlacement
  inputProps?: ComponentProps<typeof InputGroupInput>
  onProfileSelected?: (value: ProfileSearchEntry | null) => boolean | undefined
  friendsOnly?: boolean
  excludeSelf?: boolean
  maxResults?: number
}

const SFProfileSearchField = forwardRef<ComponentRef, SFProfileSearchFieldProps>((props, forwardedRef) => {
  const { t } = useTranslation()
  const formMethods = useFormContext()
  const {
    formState: { errors },
  } = formMethods

  const inputId = useId()

  const {
    name,
    required,
    label,
    helperText,
    optionalText,
    disabled,
    className,
    popOverPosition,
    inputProps,
    onProfileSelected,
    friendsOnly = false,
    excludeSelf = true,
    maxResults = 6,
    ...fieldRest
  } = props

  const [showResults, setShowResults] = useState(false)
  const [profileValue, setProfileValue] = useState('')
  const [selectedProfile, setSelectedProfile] = useState<ProfileSearchEntry | null>(null)
  const inputElRef = useRef<HTMLInputElement | null>(null)

  const { side, align, sideOffset } = popoverPlacementFromProp(popOverPosition)

  useImperativeHandle(forwardedRef, () => ({
    reset() {
      setProfileValue('')
      setSelectedProfile(null)
      if (name) {
        formMethods.setValue(name, null, { shouldDirty: false })
      }
    },
  }))

  const [profileSearch, { data, loading }] = useLazyQuery(PROFILE_SEARCH)

  const handleProfileNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const profileName = event.target.value
    setProfileValue(profileName)

    if (profileName.length > 2) {
      setShowResults(true)
      profileSearch({
        variables: {
          filter: {
            name: profileName,
            friendsOnly,
            excludeSelf,
            maxResults,
          },
          cursor: null,
        },
      })
    } else {
      setShowResults(false)
    }
  }

  if (!name) {
    throw new Error('SFProfileSearchField requires a name prop.')
  }

  const register = formMethods.register(name, {
    validate: {
      selected: () => {
        return (
          !required ||
          (selectedProfile !== null && selectedProfile !== undefined) ||
          t('Select a valid user profile from the list')
        )
      },
    },
  })

  const { ref: registerRef, onChange: registerOnChange, ...registerRest } = register

  const {
    ref: inputRefFromProps,
    className: inputClassName,
    onChange: inputOnChange,
    disabled: inputDisabledFromProps,
    ...restInputProps
  } = inputProps ?? {}

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleProfileNameChange(event)
    registerOnChange(event)
    inputOnChange?.(event)
  }

  const handleClearSelection = () => {
    setProfileValue('')
    setSelectedProfile(null)
    formMethods.setValue(name, null, { shouldDirty: false })

    if (onProfileSelected) {
      onProfileSelected(null)
    }
  }

  const handleSelectProfile = (profile: ProfileSearchEntry) => {
    setShowResults(false)
    formMethods.setValue(name, profile, { shouldDirty: false })
    setProfileValue(profile.name)
    setSelectedProfile(profile)
    if (onProfileSelected) {
      if (onProfileSelected(profile) === true) {
        handleClearSelection()
      }
    }
  }

  const handleResultsOpenChange = (open: boolean, eventDetails: { cancel: () => void; reason: string }) => {
    if (!open && eventDetails.reason === 'focus-out' && document.activeElement === inputElRef.current) {
      eventDetails.cancel()
      return
    }
    setShowResults(open)
  }

  const error = errors[name]
  const invalid = !!error

  return (
    <Field className={cn(className)} data-invalid={invalid} {...fieldRest}>
      {label !== undefined ? (
        <FieldLabel className="flex items-center gap-1" htmlFor={inputId}>
          {label}
          {required ? (
            <span aria-hidden className="text-destructive">
              *
            </span>
          ) : optionalText ? (
            <span className="font-normal text-muted-foreground text-sm">{optionalText}</span>
          ) : null}
        </FieldLabel>
      ) : null}

      <div className="relative w-full">
        <InputGroup className="w-full">
          <InputGroupAddon
            align="inline-start"
            className="w-11 min-w-11 max-w-11 justify-center border-0 bg-transparent px-1 py-0"
          >
            {selectedProfile ? (
              <SFProfileBanner disableLinks hideProfileName profile={selectedProfile} size="sm" />
            ) : (
              <Avatar className="opacity-30" size="sm">
                <AvatarFallback>
                  <UserIcon aria-hidden className="size-4 text-muted-foreground" />
                </AvatarFallback>
              </Avatar>
            )}
          </InputGroupAddon>

          <InputGroupInput
            suppressHydrationWarning
            {...registerRest}
            {...restInputProps}
            aria-invalid={invalid}
            autoComplete="off"
            className={cn(inputClassName)}
            disabled={disabled ?? inputDisabledFromProps}
            id={inputId}
            onChange={handleChange}
            ref={mergeRefs(registerRef, inputRefFromProps, inputElRef)}
            required={required}
            type="text"
            value={profileValue}
          />

          <InputGroupAddon align="inline-end">
            <InputGroupButton
              aria-label={t('Clear')}
              className="text-muted-foreground"
              onClick={handleClearSelection}
              size="icon-xs"
              type="button"
              variant="ghost"
            >
              <XIcon className="size-4" />
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>

        <Popover modal={false} onOpenChange={handleResultsOpenChange} open={showResults}>
          <PopoverTrigger nativeButton={false} render={<span className="pointer-events-none absolute inset-0" />} />
          <PopoverContent
            align={align}
            className="w-[min(100vw-2rem,24rem)]"
            finalFocus={false}
            initialFocus={false}
            side={side}
            sideOffset={sideOffset}
          >
            <PopoverHeader className="gap-1 px-1">
              <PopoverTitle>{t('Search Results')}</PopoverTitle>
            </PopoverHeader>
            <Separator className="w-full" />
            <div className="flex w-full flex-col items-stretch gap-0 px-1 pb-1">
              {loading ? (
                <div className="flex w-full justify-center self-center py-4">
                  <Spinner className="size-5" />
                </div>
              ) : null}
              {!loading && data?.profileSearch?.entries?.length === 0 ? (
                <p className="py-2 text-muted-foreground text-sm">{t('No results')}</p>
              ) : null}
              {!loading && (data?.profileSearch?.entries.length ?? 0) > 0 ? (
                <ul className="m-0 flex w-full list-none flex-col gap-0 p-0">
                  {data?.profileSearch.entries?.map((profile) => (
                    <li className="w-full" key={profile.id}>
                      <button
                        className="flex w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-transparent p-1 text-left text-foreground hover:bg-accent"
                        onClick={() => {
                          handleSelectProfile(profile)
                        }}
                        onMouseDown={(event) => {
                          event.preventDefault()
                        }}
                        type="button"
                      >
                        <SFProfileBanner disableLinks profile={profile} size="sm" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {helperText !== undefined ? <FieldDescription>{helperText}</FieldDescription> : null}
      {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
    </Field>
  )
})

SFProfileSearchField.displayName = 'SFProfileSearchField'

export default SFProfileSearchField
